import React, {useState, useRef, useEffect} from 'react';
import axios from "axios";
import {toast} from "react-toastify";
import Input from "../forms/Input";
import Button from "../Button";
import _ from "lodash";
import CreateEditCustomerModal from "../../modals/CreateEditCustomerModal";
import CreateEditSiteModal from "../../modals/CreateEditSiteModal";
import MultiFileUploader, {FILE_UPLOADER_LAYOUT} from "../multi-file-uploader/MultiFileUploader";
import {uploadToServer} from "../../helpers/FIleHelper";
import ArchiveJobModal from "../../modals/ArchiveJobModal";

const JOB_DEFAULTS = {
    customer_id:    "",
    site_id:        "",
    name:           "",
    job_type_id:    "",
    notes:          "",
    attachments:    []
}

const CreateEditJob = (props) => {
    const {
        siteId      = null,
        customerId  = null,
        jobId       = null,
        onCancel    = () => {},
        onComplete  = () => {},
        onDelete    = () => {},
        parentId    = "",
    } = props;

    const prepareDefaultValues = () => {
        const job = JOB_DEFAULTS;

        if (!!customerId && customerId !== "") {
            job.customer_id = parseInt(customerId);
        }

        if (!!siteId && siteId !== "") {
            job.site_id = parseInt(siteId);
        }

        return job;
    }

    const [submitting, setSubmitting]   = useState(false);
    const [customers, setCustomers]     = useState([]);
    const [sites, setSites]             = useState([]);
    const [jobTypes, setJobTypes]       = useState([]);
    const [job, setJob]                 = useState(prepareDefaultValues());
    const [errors, setErrors]           = useState(null);

    const [createCustomerModal, setCreateCustomerModal] = useState(false);
    const [createSiteModal, setCreateSiteModal]         = useState(false);
    const [archiveJobModal, setArchiveJobModal]         = useState(null);

    useEffect(() => {
        load(false);
    }, [jobId]);

    const load = (selectsOnly) => {
        axios.get(route('web-api.job.config', {job_id: jobId}).toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    if (!!response.data.job_types) {
                        setJobTypes(response.data.job_types);
                    }

                    if (!!response.data.customers) {
                        setCustomers(response.data.customers);
                    }

                    if (!!response.data.sites) {
                        setSites(response.data.sites);
                    }

                    if (selectsOnly === false) {
                        if (!!response.data.job) {
                            setJob(response.data.job);
                        } else {
                            setJob(prepareDefaultValues());
                        }
                    }
                }
            })
            .catch((error) => {
                console.log(error);
                toast.error(<div>{"An error occurred"}</div>);
                setSubmitting(false);
            });
    }

    useEffect(() => {
        if (!!submitting) {
            submit();
        }
    }, [submitting]);

    const reset = () => {
        setJob(JOB_DEFAULTS);

        setSubmitting(false);
        setErrors(null);
    }

    const payload = () => {
        return {
            method: !!jobId ? 'patch' : 'post',
            url:    !!jobId ? route('web-api.job.update', {job: jobId}).toString() : route('web-api.job.store').toString(),
            data:   {
                ...job,
                attachments: job.attachments.map((item) => uploadToServer(item)),
            },
        }
    }

    const submit = () => {
        axios(payload())
            .then((response) => {
                reset();
                onComplete(response.data.job_id);
            })
            .catch((error) => {
                console.log(error);
                if (!!error.response.data && !!error.response.data.errors) {
                    setErrors(error.response.data.errors);
                    toast.error(<div>{!!error.response.data.message ? error.response.data.message : "An error occurred"}</div>);
                }

                setSubmitting(false);
            });
    }

    const handleClose = () => {
        if (!submitting) {
            reset();
            onCancel();
        }
    }

    const onChange = (field, value) => {
        const clone = _.cloneDeep(job);

        switch (field) {
            case 'customer_id':
                clone.customer_id   = value;
                clone.site_id       = JOB_DEFAULTS.site_id;
                break;
            default:
                clone[field] = value;
                break;
        }

        setJob(clone);
    }

    const handleCustomerCreation = (customer) => {
        load(true);
        onChange('customer_id', customer);
        setCreateCustomerModal(false);
    }

    const handleSiteCreation = (site) => {
        load(true);
        onChange('site_id', site);
        setCreateSiteModal(false);
    }

    let selectableSites = [];
    if (!!job.customer_id && job.customer_id !== "" && !!sites) {
        selectableSites = sites.filter((site) => {
            return site.customer_id === job.customer_id;
        })
    }

    let sitePlaceholder = "Select";
    if (!job.customer_id || job.customerId === "") {
        sitePlaceholder = "Select A Customer";
    } else if (!selectableSites || selectableSites.length === 0) {
        sitePlaceholder = "No Sites"
    }

    return (
        <React.Fragment>
            <div className="row m-0 p-0">
                <div className="col-12 col-md-6 m-0 p-0 pr-md-2 bespoke-input">
                    <Input
                        name={"customer_id"}
                        label={"Customer"}
                        required={true}
                        type={"searchable-select"}
                        value={job.customer_id}
                        options={customers}
                        placeholder={"Select"}
                        disabled={!customers || customers.length < 1 || submitting}
                        onChange={(value) => onChange("customer_id", value)}
                        error={!!errors && !!errors.customer_id ? errors.customer_id : null}
                    />
                    <Button
                        onClick={() => setCreateCustomerModal(true)}
                        label={"Create New Customer"}
                        icon={"far fa-plus-circle"}
                        classes={"btn btn-eh-lilac m-0 mb-3"}
                    />
                </div>
                <div className="col-12 col-md-6 m-0 p-0 pl-md-2 bespoke-input">
                    <Input
                        name={"site_id"}
                        label={"Site"}
                        required={true}
                        type={"searchable-select"}
                        value={job.site_id}
                        options={selectableSites}
                        placeholder={sitePlaceholder}
                        disabled={!selectableSites || selectableSites.length < 1 || submitting}
                        onChange={(value) => onChange("site_id", value)}
                        error={!!errors && !!errors.site_id ? errors.site_id : null}
                    />
                    <Button
                        onClick={() => setCreateSiteModal(true)}
                        label={"Create New Site"}
                        icon={"far fa-plus-circle"}
                        classes={"btn btn-eh-lilac m-0 mb-3"}
                        disabled={!job.customer_id || job.customerId === ""}
                    />
                </div>
            </div>
            <div className="row m-0 p-0">
                <div className="col-12 col-md-6 m-0 p-0 pr-md-2 bespoke-input">
                    <Input
                        name={"name"}
                        label={"Job Name"}
                        required={true}
                        type={"text"}
                        value={job.name}
                        disabled={submitting}
                        onChange={(value) => onChange("name", value)}
                        error={!!errors && !!errors.name ? errors.name : null}
                    />
                </div>
                <div className="col-12 col-md-6 m-0 p-0 pl-md-2 bespoke-input">
                    <Input
                        name={"job_type_id"}
                        label={"Type"}
                        required={true}
                        type={"searchable-select"}
                        options={jobTypes}
                        value={job.job_type_id}
                        disabled={submitting}
                        onChange={(value) => onChange("job_type_id", value)}
                        error={!!errors && !!errors.job_type_id ? errors.job_type_id : null}
                    />
                </div>
            </div>
            <div className="row m-0 mb-4 p-0">
                <div className="col-12 m-0 p-0 bespoke-input">
                    <Input
                        name={"notes"}
                        label={"Notes"}
                        required={false}
                        type={"textarea"}
                        value={job.notes}
                        disabled={submitting}
                        onChange={(value) => onChange("notes", value)}
                        error={!!errors && !!errors.notes ? errors.notes : null}
                    />
                </div>
            </div>
            <MultiFileUploader
                title={"Attachments"}
                layout={FILE_UPLOADER_LAYOUT.ROW}
                files={job.attachments}
                onUpdate={(files) => onChange("attachments", files)}
                parentId={parentId}
            />
            <div className="row m-0 p-0">
                <div className="col-12 m-0 p-0 d-flex justify-content-between align-items-center">
                    <div className="d-flex justify-content-start align-items-center">
                        <Button
                            onClick={() => setSubmitting(true)}
                            label={!!jobId ? "Save Changes" : "Create Job"}
                            classes={"btn btn-eh-lilac m-0 mr-4"}
                        />
                        <Button
                            onClick={() => handleClose()}
                            label={"Cancel"}
                            classes={"btn btn-outline-eh-off-black m-0 mr-4"}
                        />
                    </div>
                    {!!jobId && !!job && !!job.can && job.can.delete === true && (
                        <Button
                            onClick={() => setArchiveJobModal(job)}
                            label={"Archive Job"}
                            classes={"btn btn-danger m-0"}
                        />
                    )}
                </div>
            </div>
            <CreateEditCustomerModal
                customerId={null}
                show={createCustomerModal}
                parentId={parentId}
                onCancel={() => setCreateCustomerModal(false)}
                onComplete={(customer) => handleCustomerCreation(customer)}
                onDelete={(customer) => {}}
            />
            <CreateEditSiteModal
                siteId={null}
                customerId={job.customer_id}
                show={createSiteModal}
                parentId={parentId}
                onCancel={() => setCreateSiteModal(false)}
                onComplete={(site) => handleSiteCreation(site)}
                onDelete={(site) => {}}
            />
            <ArchiveJobModal
                show={!!archiveJobModal}
                job={!!archiveJobModal ? archiveJobModal : null}
                parentId={parentId}
                onCancel={() => setArchiveJobModal(null)}
                onConfirm={() => onDelete()}
            />
        </React.Fragment>
    );
}

export default CreateEditJob;
